import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Grid,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Stack,
  Popover,
  Container,
  ButtonGroup,
  useTheme
} from '@mui/material';
import { ChromePicker } from 'react-color';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { soundService } from '../services/soundService';
import { getContrastText } from '../utils/colorUtils';
import { doc, updateDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Brightness7, Brightness4, RestartAlt, Logout, Lock } from '@mui/icons-material';
import PageLayout from './PageLayout';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile } from 'firebase/auth';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import { usePlan } from '../contexts/PlanContext';
import PlanComparisonDialog from './shared/PlanComparisonDialog';

const HABIT_SOUNDS = [
  { id: 'pop', name: 'Pop', description: 'Som de bolha estourando' },
  { id: 'click', name: 'Click', description: 'Som de clique suave' },
  { id: 'bell', name: 'Sino', description: 'Som de sino pequeno' },
  { id: 'ding', name: 'Ding', description: 'Som de campainha' },
  { id: 'chime', name: 'Chime', description: 'Som de carrilhão' },
  { id: 'coin', name: 'Moeda', description: 'Som de moeda coletada' },
  { id: 'swoosh', name: 'Swoosh', description: 'Som de movimento rápido' },
  { id: 'blip', name: 'Blip', description: 'Som de blip eletrônico' },
  { id: 'tap', name: 'Tap', description: 'Som de toque leve' },
  { id: 'none', name: 'Sem Som', description: 'Desativar som' }
];

const PERFECT_DAY_SOUNDS = [
  { id: 'achievement', name: 'Conquista', description: 'Som de conquista épica' },
  { id: 'fanfare', name: 'Fanfarra', description: 'Som de fanfarra triunfante' },
  { id: 'success', name: 'Sucesso', description: 'Som de grande sucesso' },
  { id: 'victory', name: 'Vitória', description: 'Som de vitória' },
  { id: 'levelup', name: 'Level Up', description: 'Som de subida de nível' },
  { id: 'tada', name: 'Tada', description: 'Som de celebração' },
  { id: 'applause', name: 'Aplauso', description: 'Som de aplausos' },
  { id: 'cheer', name: 'Cheer', description: 'Som de torcida' },
  { id: 'magic', name: 'Mágico', description: 'Som de efeito mágico' },
  { id: 'none', name: 'Sem Som', description: 'Desativar som' }
];

const DEFAULT_PREFERENCES = {
  mode: 'light',
  customColors: {
    primary: '#1B365D',
    secondary: '#78B6CB'
  },
  sounds: {
    habitComplete: 'pop',
    perfectDay: 'success',
    notification: 'bell',
    taskComplete: 'pop',
    goalComplete: 'achievement',
    challengeDayComplete: 'pop',
    challengeComplete: 'achievement'
  },
  highlightToday: true,
  highlightPerfectDay: true,
  calendarComponentsOrder: ['progress', 'donuts', 'area', 'stats']
};

const SOUND_OPTIONS = [
  { id: 'POP', label: 'Pop', file: '/sounds/pop.mp3' },
  { id: 'CLICK', label: 'Click', file: '/sounds/click.mp3' },
  { id: 'BELL', label: 'Sino', file: '/sounds/bell.mp3' },
  { id: 'DING', label: 'Ding', file: '/sounds/ding.mp3' },
  { id: 'CHIME', label: 'Chime', file: '/sounds/chime.mp3' },
  { id: 'COIN', label: 'Moeda', file: '/sounds/coin.mp3' },
  { id: 'SWOOSH', label: 'Swoosh', file: '/sounds/swoosh.mp3' },
  { id: 'BLIP', label: 'Blip', file: '/sounds/blip.mp3' },
  { id: 'TAP', label: 'Tap', file: '/sounds/tap.mp3' },
  { id: 'ACHIEVEMENT', label: 'Conquista', file: '/sounds/achievement.mp3' },
  { id: 'FANFARE', label: 'Fanfarra', file: '/sounds/fanfare.mp3' },
  { id: 'SUCCESS', label: 'Sucesso', file: '/sounds/success.mp3' },
  { id: 'VICTORY', label: 'Vitória', file: '/sounds/victory.mp3' },
  { id: 'LEVEL_UP', label: 'Level Up', file: '/sounds/level_up.mp3' },
  { id: 'TADA', label: 'Tada', file: '/sounds/tada.mp3' },
  { id: 'APPLAUSE', label: 'Aplauso', file: '/sounds/applause.mp3' },
  { id: 'CHEER', label: 'Cheer', file: '/sounds/cheer.mp3' },
  { id: 'MAGIC', label: 'Mágico', file: '/sounds/magic.mp3' },
  { id: 'SEM_SOM', label: 'Sem Som', file: null }
];

// Componente SoundSelector
function SoundSelector({ label, value, onChange, type }) {
  const soundOptions = type === 'perfectDay' ? PERFECT_DAY_SOUNDS : HABIT_SOUNDS;
  const selectedSound = soundOptions.find(sound => sound.id === value);

  const playSound = (soundId, event) => {
    if (event) {
      event.stopPropagation(); // Previne o fechamento do dropdown ao clicar no botão
    }
    if (soundId !== 'none') {
      const audio = new Audio(`/sounds/${soundId}.mp3`);
      audio.play().catch(console.error);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <FormControl fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value}
          onChange={onChange}
          label={label}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300
              }
            }
          }}
          renderValue={(selected) => (
            <Typography noWrap>
              {selectedSound?.name || selected}
            </Typography>
          )}
        >
          {soundOptions.map((sound) => (
            <MenuItem 
              key={sound.id} 
              value={sound.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                py: 1.5
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap>{sound.name}</Typography>
              </Box>
              {sound.id !== 'none' && (
                <Tooltip title="Testar som" placement="right">
                  <IconButton 
                    size="small"
                    onClick={(e) => playSound(sound.id, e)}
                    sx={{ 
                      ml: 'auto',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <PlayArrowIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {value !== 'none' && (
        <Tooltip title="Testar som" placement="right">
          <IconButton 
            onClick={() => playSound(value)}
            sx={{ 
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <PlayArrowIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function Settings() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { preferences, updatePreferences, resetPreferences, isLoading } = useUserPreferences();
  const { handleStop } = usePlayer();
  const { userPlan = 'BASIC', updateUserPlan } = usePlan();
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [saveButtonState, setSaveButtonState] = useState({
    text: 'Salvar',
    color: 'primary',
    disabled: false
  });
  const [pillarColorAnchor, setPillarColorAnchor] = useState(null);
  const [editingPillarId, setEditingPillarId] = useState(null);
  const [pillarResetDialogOpen, setPillarResetDialogOpen] = useState(false);
  const { mode, customColors } = preferences;

const DEFAULT_PILLAR_COLORS = {
  vitalidade: '#2E7D32',
  mentalidade: '#F9A825',
  prosperidade: '#E65100'
};

const PILLAR_LABELS = {
  vitalidade: 'Vitalidade',
  mentalidade: 'Mentalidade',
  prosperidade: 'Prosperidade'
};

  // Efeito para verificar o plano do usuário e atualizar em tempo real
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        setUserRole(userData.role || 'user');
        setUserName(userData.name || currentUser.displayName || '');
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleSaveUserName = async () => {
    if (!currentUser) {
      console.error('Usuário não está logado');
      return;
    }

    try {
      setSaveButtonState({
        text: 'Salvando...',
        color: 'primary',
        disabled: true
      });

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        name: userName,
        updatedAt: new Date().toISOString()
      });

      await updateProfile(currentUser, {
        displayName: userName
      });

      setSaveButtonState({
        text: 'Salvo!',
        color: 'success',
        disabled: true
      });

      setTimeout(() => {
        setSaveButtonState({
          text: 'Salvar',
          color: 'primary',
          disabled: false
        });
      }, 1000);

      setShowSnackbar(true);
    } catch (error) {
      console.error('Erro ao salvar nome:', error);
      setSaveButtonState({
        text: 'Erro',
        color: 'error',
        disabled: false
      });
    }
  };

  const handleModeChange = (newMode) => {
    updatePreferences({ mode: newMode });
  };

  const handleSoundChange = (event, type) => {
    const newValue = event.target.value;
    
    updatePreferences({
      ...preferences,
      sounds: {
        ...(preferences.sounds || {}),
        [type]: newValue
      }
    });
  };

  const handlePillarColorPickerOpen = (event, pillarId) => {
    setPillarColorAnchor(event.currentTarget);
    setEditingPillarId(pillarId);
  };

  const handlePillarColorPickerClose = () => {
    setPillarColorAnchor(null);
    setEditingPillarId(null);
  };

  const handlePillarColorChange = (color) => {
    if (!editingPillarId) return;
    updatePreferences({
      customColors: {
        ...customColors,
        pillars: {
          ...(customColors?.pillars || {}),
          [editingPillarId]: color.hex
        }
      }
    });
  };

  const pillarColorPickerOpen = Boolean(pillarColorAnchor);

  const handleLogout = async () => {
    try {
      // Para o áudio antes de fazer logout
      handleStop();
      
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  if (isLoading) {
    return <div>Carregando...</div>;
  }

  return (
    <PageLayout
      title="Configurações"
      hideAddButton
      customColors={customColors}
      actionButtons={
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          '& .MuiButton-root': {
            flex: { xs: 1, sm: 'initial' },
            minWidth: { xs: 0, sm: 'auto' },
            px: { xs: 1.5, sm: 3 },
            whiteSpace: 'nowrap',
            fontSize: { xs: '0.75rem', sm: '1rem' }
          }
        }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setResetDialogOpen(true)}
            startIcon={<RestartAlt />}
            sx={{ height: '40px' }}
          >
            Reiniciar Personalizações
          </Button>
        </Box>
      }
    >
      <Grid container spacing={3}>
        {/* Perfil e Aparência */}
        <Grid item xs={12} lg={6}>
          {/* Seção de Perfil */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                mb: 2
              }}
            >
              Perfil
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 2,
              flexDirection: 'column'
            }}>
              <TextField
                fullWidth
                label="Seu nome"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                variant="outlined"
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main'
                    }
                  }
                }}
              />
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                width: '100%',
                flexWrap: 'wrap'
              }}>
                <Button
                  variant="contained"
                  onClick={handleSaveUserName}
                  disabled={saveButtonState.disabled}
                  color={saveButtonState.color}
                  sx={{ 
                    flex: { xs: 1, sm: 'initial' },
                    height: '40px',
                    minWidth: { xs: 'auto', sm: '120px' },
                    bgcolor: saveButtonState.color === 'success' ? '#4caf50' : 'primary.main',
                    color: '#ffffff',
                    '&:hover': {
                      bgcolor: saveButtonState.color === 'success' ? '#43a047' : 'primary.main',
                      opacity: 0.9
                    }
                  }}
                >
                  {saveButtonState.text}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/alterar-senha')}
                  startIcon={<Lock />}
                  sx={{ 
                    flex: { xs: 1, sm: 'initial' },
                    height: '40px',
                    whiteSpace: 'nowrap',
                    minWidth: { xs: 'auto', sm: '140px' },
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.main' + '15'
                    }
                  }}
                >
                  Alterar senha
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                  sx={{ 
                    flex: { xs: 1, sm: 'initial' },
                    height: '40px',
                    whiteSpace: 'nowrap',
                    minWidth: { xs: 'auto', sm: '160px' }
                  }}
                >
                  Sair da conta
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Seção de Plano */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                mb: 2
              }}
            >
              Plano
            </Typography>
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="subtitle1">
                  Plano Atual:
                </Typography>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600,
                    color: userPlan === 'PRO' ? 'success.main' : 'primary.main'
                  }}
                >
                  {userRole === 'admin' ? 'ADMIN' : userPlan === 'PRO' ? 'PRO' : 'BÁSICO'}
                </Typography>
              </Box>

              {userPlan !== 'PRO' && userRole !== 'admin' && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Faça upgrade para o plano PRO e tenha acesso a:
                  </Typography>
                  <Box component="ul" sx={{ 
                    pl: 2,
                    m: 0,
                    '& li': { mb: 1 }
                  }}>
                    <li>Criação de hábitos ilimitados</li>
                    <li>Criação de metas ilimitadas</li>
                    <li>Criação de desafios ilimitados</li>
                    <li>Criação de notas ilimitadas</li>
                    <li>Criação de eventos ilimitados</li>
                    <li>Estatísticas avançadas</li>
                    <li>E muito mais!</li>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 1,
                      bgcolor: customColors?.primary || 'primary.main',
                      color: customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText,
                      '&:hover': {
                        bgcolor: customColors?.primary || 'primary.main',
                        opacity: 0.9
                      }
                    }}
                    onClick={() => setShowPlanDialog(true)}
                  >
                    Fazer Upgrade para PRO
                  </Button>
                </>
              )}
            </Box>
          </Paper>

          {/* Seção de Aparência */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                mb: 2
              }}
            >
              Aparência
            </Typography>
            
            <Grid container spacing={3}>
              {/* Seletor de Tema */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Modo do Tema
                </Typography>
                <ButtonGroup 
                  variant="outlined" 
                  sx={{ 
                    width: '100%',
                    '& .MuiButton-root': {
                      flex: 1
                    }
                  }}
                >
                  <Button
                    onClick={() => handleModeChange('light')}
                    variant={mode === 'light' ? 'contained' : 'outlined'}
                    sx={{ height: '40px' }}
                  >
                    Claro
                  </Button>
                  <Button
                    onClick={() => handleModeChange('dark')}
                    variant={mode === 'dark' ? 'contained' : 'outlined'}
                  >
                    Escuro
                  </Button>
                </ButtonGroup>
              </Grid>

              {/* Cores dos 3 Pilares */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1, mb: 1 }}>
                  Cores dos 3 Pilares (Life OS)
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(DEFAULT_PILLAR_COLORS).map(([pillarId, defaultHex]) => (
                    <Grid item xs={12} sm={6} md={3} key={pillarId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Button
                          variant="outlined"
                          onClick={(e) => handlePillarColorPickerOpen(e, pillarId)}
                          sx={{
                            height: '38px',
                            minWidth: 48,
                            p: 0,
                            borderColor: 'divider',
                            '&:hover': {
                              borderColor: customColors?.pillars?.[pillarId] || defaultHex
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              bgcolor: customColors?.pillars?.[pillarId] || defaultHex,
                              border: '2px solid',
                              borderColor: 'divider'
                            }}
                          />
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          {PILLAR_LABELS[pillarId]}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPillarResetDialogOpen(true)}
                  >
                    Restaurar cores padrão dos pilares
                  </Button>
                </Box>
                <Popover
                  open={pillarColorPickerOpen}
                  anchorEl={pillarColorAnchor}
                  onClose={handlePillarColorPickerClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                  sx={{ mt: 1 }}
                >
                  <Box sx={{ p: 1 }}>
                    {editingPillarId && (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, px: 0.5 }}>
                          {PILLAR_LABELS[editingPillarId]}
                        </Typography>
                        <ChromePicker
                          color={customColors?.pillars?.[editingPillarId] || DEFAULT_PILLAR_COLORS[editingPillarId]}
                          onChange={handlePillarColorChange}
                          disableAlpha={true}
                          styles={{ default: { picker: { width: '225px' } } }}
                        />
                      </>
                    )}
                  </Box>
                </Popover>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Sons e Notificações */}
        <Grid item xs={12} lg={6}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                mb: 3
              }}
            >
              Sons e Notificações
            </Typography>
            
            <Stack spacing={3}>
              <SoundSelector
                label="Som ao completar hábito"
                value={preferences?.sounds?.habitComplete || 'pop'}
                onChange={(e) => handleSoundChange(e, 'habitComplete')}
                type="habit"
              />
              <SoundSelector
                label="Som ao completar todos os hábitos do dia"
                value={preferences?.sounds?.perfectDay || 'achievement'}
                onChange={(e) => handleSoundChange(e, 'perfectDay')}
                type="perfectDay"
              />
              <SoundSelector
                label="Som ao completar tarefa de meta"
                value={preferences?.sounds?.taskComplete || 'pop'}
                onChange={(e) => handleSoundChange(e, 'taskComplete')}
                type="habit"
              />
              <SoundSelector
                label="Som ao completar meta"
                value={preferences?.sounds?.goalComplete || 'achievement'}
                onChange={(e) => handleSoundChange(e, 'goalComplete')}
                type="perfectDay"
              />
              <SoundSelector
                label="Som ao completar dia do desafio"
                value={preferences?.sounds?.challengeDayComplete || 'pop'}
                onChange={(e) => handleSoundChange(e, 'challengeDayComplete')}
                type="habit"
              />
              <SoundSelector
                label="Som ao completar desafio"
                value={preferences?.sounds?.challengeComplete || 'achievement'}
                onChange={(e) => handleSoundChange(e, 'challengeComplete')}
                type="perfectDay"
              />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Diálogo de confirmação para reiniciar personalizações */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Confirmar Reinicialização</DialogTitle>
        <DialogContent>
          <Typography color="error" paragraph>
            ATENÇÃO: Esta ação irá:
          </Typography>
          <ul>
            <li>Restaurar o tema para o modo claro</li>
            <li>Redefinir as cores para o padrão</li>
            <li>Reativar todos os destaques e sons</li>
            <li>Restaurar o layout da sessão Calendário</li>
          </ul>
          <Typography paragraph>
            Tem certeza que deseja reiniciar todas as personalizações?
          </Typography>
          <Typography color="error">
           
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={async () => {
              await resetPreferences();
              setResetDialogOpen(false);
            }} 
            color="error" 
            variant="contained"
          >
            Reiniciar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação para restaurar cores padrão dos pilares */}
      <Dialog open={pillarResetDialogOpen} onClose={() => setPillarResetDialogOpen(false)}>
        <DialogTitle>Restaurar cores dos pilares?</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            As cores personalizadas dos 3 pilares serão substituídas pelas cores padrão da Habitop.
          </Typography>
          <Typography color="error">
            Esta ação não pode ser desfeita (mas você pode personalizar as cores novamente depois).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPillarResetDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              updatePreferences({
                customColors: {
                  ...customColors,
                  pillars: { ...DEFAULT_PILLAR_COLORS }
                }
              });
              setPillarResetDialogOpen(false);
            }}
            color="primary"
            variant="contained"
          >
            Restaurar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Nome salvo com sucesso!
        </Alert>
      </Snackbar>

      {/* Diálogo de comparação de planos */}
      <PlanComparisonDialog
        open={showPlanDialog}
        onClose={() => setShowPlanDialog(false)}
        title="Upgrade para o Plano PRO"
        customColors={customColors}
      />
    </PageLayout>
  );
}

export default Settings; 